import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CartContext = createContext();

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], addons: [] });
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchCart = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cart`, { withCredentials: true });
      setCart(res.data);
    } catch {
      setCart({ items: [], addons: [] });
    }
  }, []);

  useEffect(() => {
    fetchCart();
  }, [fetchCart, user]);

  const openCartDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeCartDrawer = useCallback(() => setDrawerOpen(false), []);
  const toggleCartDrawer = useCallback(() => setDrawerOpen((v) => !v), []);

  const addToCart = useCallback(async (productId, quantity = 1, addons = [], opts = {}) => {
    setLoading(true);
    try {
      const payload = { product_id: productId, quantity, addons };
      if (opts.variant_id) payload.variant_id = opts.variant_id;
      await axios.post(`${API}/cart/items`, payload, { withCredentials: true });
      await fetchCart();
      if (opts.openDrawer !== false) setDrawerOpen(true);
    } finally {
      setLoading(false);
    }
  }, [fetchCart]);

  const removeFromCart = useCallback(async (itemId) => {
    setLoading(true);
    try {
      await axios.delete(`${API}/cart/items/${itemId}`, { withCredentials: true });
      await fetchCart();
    } finally {
      setLoading(false);
    }
  }, [fetchCart]);

  const updateCartItem = useCallback(async (itemId, data) => {
    try {
      await axios.put(`${API}/cart/items/${itemId}`, data, { withCredentials: true });
      await fetchCart();
    } catch { /* ignore */ }
  }, [fetchCart]);

  const itemCount = cart?.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0;

  return (
    <CartContext.Provider value={{
      cart, loading, addToCart, removeFromCart, updateCartItem, fetchCart, itemCount,
      drawerOpen, openCartDrawer, closeCartDrawer, toggleCartDrawer
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
